import React from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import type { KambiSearchTermsView } from '@mobi/api-types/kambi'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { colors } from '@mobi/component-library/Foundations'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'
import { E_SOCCER_GROUP_ID } from '@mobi/sports-offering/helpers/reformatSportsName'
import { addSearchTermToPrevious } from '@core/Areas/Search/Store'
import { OverlayClose } from '@core/Components/Overlay'
import { trackSearch, trackSearchInteraction } from '../../helpers/analytics'
import { EmptyScreen } from '../Common/EmptyScreen'
import { LoadingSkeleton } from '../Common/LoadingSkeleton'
import { NoSearchResults } from '../Common/NoSearchResults'
import { ResultContentListRow } from '../Common/ResultContentListRow'
import { SearchErrored } from '../Common/SearchErrored'

export const SportsSearch: React.FC<{
  isError: boolean
  isLoading: boolean
  searchTerm: string
  handleImmediateSearch: (searchTerm: string) => void
  searchWasImmediate: boolean
  searchResults?: KambiSearchTermsView
}> = ({
  isError,
  isLoading,
  searchResults,
  searchTerm,
  handleImmediateSearch,
  searchWasImmediate,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (isError || isLoading || !searchResults) {
      return
    }
    trackSearch({
      interaction_type: 'Search Results Viewed',
      search_term: searchTerm.toLowerCase(),
      resultsFound: searchResults.resultTerms.length > 0,
      searchOrigin: searchWasImmediate ? 'pre-filled' : 'manual',
    })
  }, [isError, isLoading, searchResults, searchTerm, searchWasImmediate])

  if (isError) {
    return <SearchErrored />
  }
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!searchResults) {
    return <EmptyScreen searchMode='sports' handleImmediateSearch={handleImmediateSearch} />
  }

  if (searchResults.resultTerms.length === 0) {
    return <NoSearchResults />
  }

  const handleResultClick = (searchResult: KambiSearchTermsView['resultTerms']['0']) => {
    dispatch(addSearchTermToPrevious({ type: 'sports', searchTerm }))
    OverlayClose()
    if (searchResult.type === 'REGION' && searchResult.parentId) {
      navigate('/sports' + searchResult.parentId)
    } else {
      navigate('/sports' + searchResult.id)
    }

    trackSearchInteraction({
      interaction_type: 'Search Result Clicked',
      search_term: searchTerm.toLowerCase(),
      sportCode: searchResult.sportCode || searchResult.secondaryValue?.split(' - ')[0],
      competition: searchResult.competition || searchResult.secondaryValue?.split(' - ')[1],
    })
  }

  return (
    <List variant='card' itemSize='dynamic'>
      {searchResults.resultTerms.map(searchResult => {
        return (
          <ListItem
            key={searchResult.id}
            onClick={() => handleResultClick(searchResult)}
            testId='search-result'
            icon={
              <SportsIcon
                color={colors.neutral[800]}
                sport={searchResult.icon}
                size='2rem'
                groupId={
                  searchResult.id.includes('esports_football') ? E_SOCCER_GROUP_ID : undefined
                }
              />
            }
            content={
              <ResultContentListRow
                searchTerm={searchResults.term}
                primaryText={searchResult.value}
                secondaryText={searchResult.secondaryValue}
              />
            }
          />
        )
      })}
    </List>
  )
}
